"use client";

import { TIndustriesConfig } from "@/app/config";
import React from "react";
import { motion } from "motion/react";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
      duration: 0.6
    }
  }
};
const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};


export default function ContentGenrePerformance({config}:{config: TIndustriesConfig['contentGenrePerformance']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-gray-50 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={containerVariants}
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-purple-50/40 to-blue-50/40"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
      />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={ {
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94]
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.1 
            }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.2 
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Genre Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6"
          variants={gridVariants}
        >
          {config.genreData.map((genre, index) => (
            <motion.div
              key={index}
              className={`${genre.bgColor} ${genre.borderColor} border rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 relative overflow-hidden cursor-pointer`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 60,
                  scale: 0.9,
                  rotateY: -15
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  rotateY: 0,
                  transition: {
                    duration: 0.7,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    staggerChildren: 0.1,
                    delayChildren: 0.2
                  }
                }
              }}
              whileHover={ {
                scale: 1.03,
                y: -8,
                rotateY: 2,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.05)",
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
              style={{ perspective: 1000 }}
            >
              {/* Background glow effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"
                variants={{
                  hidden: { opacity: 0, scale: 0.8 },
                  visible: {
                    opacity: 0.1,
                    scale: 1,
                    transition: {
                      duration: 1,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    opacity: 0.2,
                    scale: 1.05,
                    transition: {
                      duration: 0.3
                    }
                  }
                }}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              />

              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                variants={ {
                  hidden: { x: "-100%" },
                  visible: {
                    x: "100%",
                    transition: {
                      duration: 2,
                      ease: "easeInOut",
                      repeat: Infinity,
                      repeatDelay: 3
                    }
                  }
                }}
                initial="hidden"
                animate="visible"
                style={{
                  transform: "skewX(-20deg)"
                }}
              />

              {/* Revenue Badge */}
              <motion.div 
                className="absolute top-4 right-4 z-20"
                variants={ {
                  hidden: { 
                    opacity: 0, 
                    scale: 0,
                    rotate: -180
                  },
                  visible: {
                    opacity: 1,
                    scale: 1,
                    rotate: 0,
                    transition: {
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                      delay: 0.2
                    }
                  },
                  hover: {
                    scale: 1.1,
                    rotate: 5,
                    transition: {
                      duration: 0.2,
                      ease: "easeOut"
                    }
                  }
                }}
                whileHover="hover"
              >
                <motion.span
                  className={`${genre.badgeColor} text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg`}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                    delay: index * 0.15 + 0.3
                  }}
                >
                  {genre.revenueShare}
                </motion.span>
              </motion.div>

              {/* Genre Title */}
              <motion.div 
                className="mb-6 relative z-10"
                variants={{
                  hidden: { opacity: 0, x: -30 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }
                }}
              >
                <motion.h3 
                  className={`text-xl font-semibold ${genre.titleColor} mb-2`}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.6,
                    ease: "easeOut",
                    delay: index * 0.15 + 0.1
                  }}
                >
                  {genre.title}
                </motion.h3>
              </motion.div>

              {/* Audience Popularity */}
              <motion.div 
                className="mb-6 relative z-10"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut",
                      staggerChildren: 0.1
                    }
                  }
                }}
              >
                <motion.div 
                  className="flex justify-between items-center mb-2"
                  variants={{
                    hidden: { opacity: 0, scale: 0.8 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.4,
                        delay: 0.3,
                        ease: "backOut"
                      }
                    }
                  }}
                >
                  <motion.span 
                    className="text-sm text-gray-700 font-medium"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.15 + 0.2
                    }}
                  >
                    Audience Popularity
                  </motion.span>
                  <motion.span 
                    className="text-sm font-bold text-gray-900"
                    initial={{ opacity: 0, scale: 0.6 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: index * 0.15 + 0.4
                    }}
                  >
                    {genre.audiencePopularity}/100
                  </motion.span>
                </motion.div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden relative">
                  <motion.div
                    className="bg-gray-800 h-3 rounded-full relative overflow-hidden"
                    variants={{
                      hidden: { width: "0%" },
                      visible: (popularity: number) => ({
                        width: `${popularity}%`,
                        transition: {
                          duration: 1.2,
                          delay: 0.5,
                          ease: [0.25, 0.46, 0.45, 0.94]
                        }
                      })
                    }}
                    custom={genre.audiencePopularity}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    {/* Progress bar shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                      animate={{
                        x: ["-100%", "100%"]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatDelay: 1,
                        ease: "easeInOut",
                        delay: index * 0.15 + 1
                      }}
                    />
                  </motion.div>
                </div>
              </motion.div>

              {/* Primary Platforms */}
              <motion.div 
                className="relative z-10"
                variants={{
                  hidden: { opacity: 0, y: 15 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut",
                      delay: 0.4
                    }
                  }
                }}
              >
                <motion.p 
                  className="text-sm text-gray-700 font-medium mb-1"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.15 + 0.6
                  }}
                >
                  Primary Platforms
                </motion.p>
                <motion.p 
                  className="text-sm text-gray-600"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.15 + 0.7
                  }}
                >
                  {genre.primaryPlatforms}
                </motion.p>
              </motion.div>

              {/* Floating decoration */}
              <motion.div
                className="absolute -bottom-2 -right-2 w-20 h-20 bg-gradient-to-br from-blue-100/20 to-purple-100/20 rounded-full"
                animate={{
                  y: [0, -10, 0],
                  rotate: [0, 5, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                  delay: index * 0.5
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}